import React, { Suspense, lazy, useEffect } from "react";
import { Route, BrowserRouter as Router, Routes, useLocation, useParams, useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { withProfiler } from "@sentry/react";

import Toaster from "components/toasters";

import { removeHubspotConversations, trackPageView } from "utils/hubspot";
import { InitializeExternalApp } from "InitializeExternalApp";

const InitializeGameApp = lazy(() => import("InitializeGameApp"));
const InitializeAdminApp = lazy(() => import("InitializeAdminApp"));

const OidcInteractionPage: React.FC = () => {
	const { uid } = useParams<{ uid: string }>();
	const navigate = useNavigate();
	const formRef = React.useRef<HTMLFormElement>(null);
	const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);

	const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin + "/api";
	const oidcLoginUrl = `${apiBaseUrl}/oidc/interaction/${uid}/login`;

	useEffect(() => {
		// Check if user is already authenticated via session cookie
		fetch(`${apiBaseUrl}/auth/me`, { credentials: "include" })
			.then((res) => {
				if (res.ok) {
					setIsAuthenticated(true);
				} else {
					navigate(`/admin/login?oidc_uid=${uid}`, { replace: true });
				}
			})
			.catch(() => {
				navigate(`/admin/login?oidc_uid=${uid}`, { replace: true });
			});
	}, [uid, navigate, apiBaseUrl]);

	// Auto-submit the hidden form once we know the user is authenticated
	useEffect(() => {
		if (isAuthenticated && formRef.current) {
			formRef.current.submit();
		}
	}, [isAuthenticated]);

	// Render a hidden form that POSTs to the OIDC login endpoint.
	// The browser natively follows the 303 redirect chain from oidc-provider.
	if (isAuthenticated) {
		return <form ref={formRef} method="POST" action={oidcLoginUrl} style={{ display: "none" }} />;
	}

	return null;
};

const App: React.FC = () => {
	return (
		<Router>
			<MainApp />
		</Router>
	);
};

const MainApp = () => {
	const location = useLocation();

	useEffect(() => {
		// track page view for hubspot
		trackPageView();

		if (!location.pathname.includes("subscription-plan")) {
			removeHubspotConversations();
		}
	}, [location]);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Toaster>
				<Suspense fallback={
					<div className="loading-skeleton">
						<div className="spinner"></div>
					</div>
				}>
					<Routes>
						<Route path="/oidc/interaction/:uid" element={<OidcInteractionPage />} />
						<Route path="/admin/*" element={<InitializeAdminApp />} />
						<Route path="/*" element={<InitializeGameApp />} />
						<Route path="/external/*" element={<InitializeExternalApp />} />
					</Routes>
				</Suspense>
			</Toaster>
		</LocalizationProvider>
	);
};

export default withProfiler(App);
